/**
 * Snapshot-Panel: Admin sieht das Probst-Portal in der Sicht eines ausgewaehlten Testers.
 *
 * - Dropdown listet alle User mit Permission tests.run
 * - Beim Aktivieren setzt der Server den Cookie viewAsTester (path-restricted, /).
 *   Der Cookie wirkt nur AUSSERHALB von /api/test-cockpit/, sodass das Cockpit selbst
 *   weiterhin Joergs Sicht zeigt — das eingebettete Iframe rendert den View-Subject.
 * - Iframe-URL ist der Probst-Portal-Pfad. Joerg klickt im Iframe wie ein Tester.
 * - "Beenden" entfernt den Cookie + reload das Iframe.
 */
import { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Loader2, RefreshCw, ExternalLink } from 'lucide-react';
import { testCockpitApi, type Tester } from '../../../lib/testCockpitApi';

const IFRAME_PATHS = [
  { label: 'Dashboard', path: '/' },
  { label: 'Bauberichte', path: '/m/bauberichteV2/dashboard' },
  { label: 'Berichte-Liste', path: '/m/bauberichteV2/reports' },
  { label: 'Aufgaben', path: '/m/bauberichteV2/tasks' },
  { label: 'Plantafel', path: '/m/plantafel' },
  { label: 'Auswertungen', path: '/m/disposition' },
];

export default function TesterSnapshotPanel() {
  const [testers, setTesters] = useState<Tester[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [iframePath, setIframePath] = useState<string>('/');
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    testCockpitApi.listTesters()
      .then(setTesters)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function start(testerId: string) {
    setBusy(true);
    try {
      await testCockpitApi.viewAsStart(testerId);
      setActive(testerId);
    } catch (e) {
      alert(`View-as starten fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function stop() {
    setBusy(true);
    try {
      await testCockpitApi.viewAsStop();
      setActive(null);
    } catch (e) {
      alert(`View-as beenden fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  function reloadIframe() {
    if (iframeRef.current) {
      const cur = iframeRef.current.src;
      iframeRef.current.src = 'about:blank';
      window.setTimeout(() => { if (iframeRef.current) iframeRef.current.src = cur; }, 50);
    }
  }

  const activeTester = testers.find(t => t.id === active);
  // Iframe-URL: enthaelt _view-as Param. apiFetch.ts im Iframe-Bundle liest ihn und
  // haengt X-View-As-Tester an alle API-Calls. Der Cockpit-Tab schickt diesen
  // Header NICHT — sieht weiter Joerg.
  const iframeSrc = active ? `${iframePath}${iframePath.includes('?') ? '&' : '?'}_view-as=${active}` : 'about:blank';

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground py-3"><Loader2 size={14} className="animate-spin" /> Tester laden...</div>;
  }
  if (testers.length === 0) {
    return <div className="text-sm text-muted-foreground py-3">Keine Tester mit Permission <code>tests.run</code> verfügbar.</div>;
  }

  return (
    <div className="bg-card border border-default rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Eye size={16} className="text-brand-500" />
          <span className="font-medium text-sm">Snapshot eines Testers</span>
          {active && activeTester && (
            <span className="text-xs px-2 py-0.5 rounded bg-brand-500/15 text-brand-700 dark:text-brand-300">
              aktiv: {activeTester.name || activeTester.email}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={active ?? ''}
            onChange={(e) => { const v = e.target.value; if (v) start(v); }}
            disabled={busy}
            className="px-2 py-1.5 rounded bg-background border border-default text-sm"
          >
            <option value="">Tester waehlen...</option>
            {testers.map(t => (
              <option key={t.id} value={t.id}>{t.name || t.email}</option>
            ))}
          </select>
          {active && (
            <button
              onClick={stop}
              disabled={busy}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card border border-default text-sm hover:bg-rose-500/10 text-rose-600 dark:text-rose-400"
              title="View-as beenden"
            >
              <EyeOff size={14} /> Beenden
            </button>
          )}
        </div>
      </div>

      {active && (
        <>
          <div className="flex items-center gap-2 text-xs flex-wrap border-t border-default/50 pt-2">
            <span className="text-muted-foreground">Sprung:</span>
            {IFRAME_PATHS.map(p => (
              <button
                key={p.path}
                onClick={() => setIframePath(p.path)}
                className={`px-2 py-1 rounded ${iframePath === p.path ? 'bg-primary/15 text-primary' : 'hover:bg-primary/5 text-muted-foreground'}`}
              >
                {p.label}
              </button>
            ))}
            <button
              onClick={reloadIframe}
              className="ml-auto flex items-center gap-1 px-2 py-1 rounded hover:bg-primary/5 text-muted-foreground"
              title="Iframe neu laden"
            >
              <RefreshCw size={12} /> Reload
            </button>
            <a
              href={iframePath}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-2 py-1 rounded hover:bg-primary/5 text-muted-foreground"
              title="Im neuen Tab oeffnen — vorsicht: oeffnet in DEINER Sicht (kein view-as)"
            >
              <ExternalLink size={12} />
            </a>
          </div>
          <div className="border border-default rounded overflow-hidden bg-background">
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              title={`Snapshot ${activeTester?.name || activeTester?.email || ''}`}
              className="w-full"
              style={{ height: '720px', border: 'none' }}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            />
          </div>
          <div className="text-xs text-muted-foreground">
            Read-only-Vorschau: der Iframe rendert die App so, wie {activeTester?.name || activeTester?.email} sie sieht.
            Klicken zur Navigation funktioniert; Schreib-Aktionen (Speichern, Loeschen, Persona-Wechsel) werden vom Backend mit 403 abgelehnt.
          </div>
        </>
      )}
    </div>
  );
}
